import React, { useState } from "react";

import { featureFlags } from "~/common/feature-flags";
import { pushSupported, subscribeToPush } from "~/common/push-subscribe";
import { isAndroid, isIOS, isStandalone } from "~/common/pwa";
import { markDismissed, wasRecentlyDismissed } from "~/common/pwa-onboarding-storage";
import { useInstallPrompt } from "~/hooks/use-install-prompt";
import { selectUser } from "~/slices/user";
import { useAppSelector } from "~/store";

import { RedesignEnablePushModal } from "./enable-push-modal";
import { RedesignInstallAppModal } from "./install-app-modal";

function currentPermission(): NotificationPermission {
  return typeof window !== "undefined" && "Notification" in window ? Notification.permission : "default";
}

export function PwaOnboarding() {
  const user = useAppSelector(selectUser);
  const [standalone] = useState(isStandalone);
  const [ios] = useState(isIOS);
  const [android] = useState(isAndroid);
  const { canInstall, promptInstall } = useInstallPrompt();
  const [installDismissed, setInstallDismissed] = useState(() => wasRecentlyDismissed("install"));
  const [pushPrimerDismissed, setPushPrimerDismissed] = useState(() => wasRecentlyDismissed("push_primer"));
  const [pushBusy, setPushBusy] = useState(false);
  const [permission, setPermission] = useState(currentPermission);

  if (!user.account_id || !featureFlags.pushNotifications || !featureFlags.redesignV1 || !featureFlags.pwaOnboarding) return null;

  const showInstall = !standalone && !installDismissed && (ios || (android && canInstall));
  const showPushPrimer = !showInstall && !pushPrimerDismissed && pushSupported() && permission === "default";

  async function handleInstall() {
    await promptInstall();
    markDismissed("install");
    setInstallDismissed(true);
  }

  function handleInstallDismiss() {
    markDismissed("install");
    setInstallDismissed(true);
  }

  async function handleEnablePush() {
    setPushBusy(true);
    try {
      await subscribeToPush();
    } catch {
      // `Notification.permission` below reflects the outcome; the profile page's own push section
      // is where a denied/failed subscribe gets a persistent state, not this one-shot primer.
    } finally {
      setPushBusy(false);
      setPermission(currentPermission());
      markDismissed("push_primer");
      setPushPrimerDismissed(true);
    }
  }

  function handlePushDismiss() {
    markDismissed("push_primer");
    setPushPrimerDismissed(true);
  }

  return (
    <>
      <RedesignInstallAppModal open={showInstall} ios={ios} onInstall={handleInstall} onDismiss={handleInstallDismiss} />
      <RedesignEnablePushModal open={showPushPrimer} busy={pushBusy} onEnable={handleEnablePush} onDismiss={handlePushDismiss} />
    </>
  );
}
